// Session maths (SPEC.md §4.3 sessions). Pure functions over plain rows.
import type { Modality, PlannedSession } from './schedule';

export type SessionStatus = 'in_progress' | 'completed' | 'missed' | 'ended_early';

export const STATUS_LABELS: Record<SessionStatus, string> = {
  in_progress: 'In progress',
  completed: 'Completed',
  missed: 'Missed',
  ended_early: 'Ended early'
};

export interface SessionRow {
  id: string;
  session_date: string;
  modality: Modality;
  status: SessionStatus;
  start_time: string | null;
  end_time: string | null;
  duration_min: number | null;
  pre_weight_kg: number | null;
  post_weight_kg: number | null;
  dry_weight_kg: number | null;
  uf_goal_ml: number | null;
  uf_achieved_ml: number | null;
  early_end_reason: string | null;
  missed_reason: string | null;
  notes: string | null;
  blood_flow_ml_min: number | null;
  dialysate_flow_ml_min: number | null;
  dialyzer: string | null;
  heparin_dose: string | null;
  needle_gauge: string | null;
  venous_pressure: number | null;
  arterial_pressure: number | null;
  tmp: number | null;
  conductivity: number | null;
}

const round1 = (value: number) => Math.round(value * 10) / 10;

/** Chronological order: date, then start time (sessions without a time sort first that day). */
export function compareSessions(a: Pick<SessionRow, 'session_date' | 'start_time'>, b: Pick<SessionRow, 'session_date' | 'start_time'>) {
  if (a.session_date !== b.session_date) return a.session_date < b.session_date ? -1 : 1;
  return (a.start_time ?? '').localeCompare(b.start_time ?? '');
}

export function weightRemoved(session: Pick<SessionRow, 'pre_weight_kg' | 'post_weight_kg'>): number | null {
  if (session.pre_weight_kg == null || session.post_weight_kg == null) return null;
  return round1(session.pre_weight_kg - session.post_weight_kg);
}

/**
 * Interdialytic weight gain for every session that has a pre-weight: its pre-weight minus the
 * post-weight of the latest earlier session that recorded one. Missed sessions are skipped over,
 * so the gain after a missed session covers the whole gap.
 */
export function interdialyticGains(sessions: SessionRow[]): Map<string, number> {
  const gains = new Map<string, number>();
  let lastPost: number | null = null;
  for (const session of [...sessions].sort(compareSessions)) {
    if (session.pre_weight_kg != null && lastPost != null) gains.set(session.id, round1(session.pre_weight_kg - lastPost));
    if (session.post_weight_kg != null) lastPost = session.post_weight_kg;
  }
  return gains;
}

/** Minutes between two 'HH:mm' times; an end before the start is taken as past midnight. */
export function minutesBetween(start: string | null, end: string | null): number | null {
  if (!start || !end) return null;
  const toMinutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
  const diff = toMinutes(end) - toMinutes(start);
  return diff >= 0 ? diff : diff + 24 * 60;
}

/** Planned sessions before `today` with nothing logged on their date (any status counts as logged). */
export function unloggedPlanned(planned: PlannedSession[], sessions: Pick<SessionRow, 'session_date'>[], today: string): PlannedSession[] {
  const logged = new Set(sessions.map((session) => session.session_date));
  return planned.filter((session) => session.date < today && !logged.has(session.date));
}

export function formatKg(value: number | null | undefined, signed = false) {
  if (value == null) return '–';
  const text = `${Math.abs(value).toFixed(1)} kg`;
  if (!signed) return `${value < 0 ? '−' : ''}${text}`;
  return `${value > 0 ? '+' : value < 0 ? '−' : '±'}${text}`;
}
